// Entrada no painel: login, conclusão do link mágico (`/auth/email-link`) e
// a barreira de papel (`AuthGuard`).
import type { ReactNode } from 'react';
import { defineMessages } from '../core';

export const authT = defineMessages('auth', {
  pt: {
    login: {
      subtitle: 'Use seu Google ou email cadastrado pela organização.',
      /// Só aparece em build sem as variáveis do Firebase (dev/CI). Os nomes
      /// de variável e de arquivo chegam prontos, com a marcação de código.
      firebaseNotConfigured: (env: ReactNode, file: ReactNode, doc: ReactNode) => (
        <>
          Firebase auth não configurada nesta build. Defina {env} em {file} e rebuilde. Veja {doc}.
        </>
      ),
      continueWithGoogle: 'Continuar com Google',
      or: 'ou',
      emailLabel: 'Email',
      passwordLabel: 'Senha',
      signIn: 'Entrar',
      privacyPolicy: 'Política de privacidade',
      terms: 'Termos de uso',
    },
    emailLink: {
      title: 'WindMaps Admin',
      confirming: 'Confirmando seu acesso…',
      validating: 'Validando link de acesso…',
      success: 'Entrou. Redirecionando…',
      confirmEmailLabel: 'Confirme seu email para concluir o acesso',
      emailPlaceholder: 'seu-email@exemplo.com',
      emailWhy: 'O Firebase exige isso por segurança (anti-phishing).',
      signIn: 'Entrar',
      failedTitle: 'Não foi possível entrar',
      goToLogin: 'Ir para login',
      errors: {
        notConfigured: 'Firebase não está configurada neste ambiente. Recarregue ou contate o suporte.',
        invalidLink: 'Link inválido ou expirado. Peça um novo convite ao administrador.',
        expiredOrUsed: 'O link expirou ou já foi usado. Peça um novo convite ao administrador.',
        invalidEmail: 'Email inválido. Verifique o endereço digitado.',
        userDisabled: 'Usuário desabilitado. Contate o administrador.',
        network: 'Sem conexão. Verifique sua internet e tente novamente.',
        unexpected: 'Erro inesperado. Tente novamente em alguns instantes.',
      },
    },
    guard: {
      restrictedTitle: 'Acesso restrito',
      /// `role` chega formatado (código), e é o nome técnico do papel: não
      /// se traduz, é o que o super-admin procura na gestão de usuários.
      restrictedBody: (role: ReactNode) => (
        <>
          Esta área requer o papel {role}. Peça à organização para promover sua conta.
        </>
      ),
    },
  },
  en: {
    login: {
      subtitle: 'Use your Google account or the email registered by the organization.',
      firebaseNotConfigured: (env, file, doc) => (
        <>
          Firebase auth is not configured in this build. Set {env} in {file} and rebuild. See {doc}.
        </>
      ),
      continueWithGoogle: 'Continue with Google',
      or: 'or',
      emailLabel: 'Email',
      passwordLabel: 'Password',
      signIn: 'Sign in',
      privacyPolicy: 'Privacy policy',
      terms: 'Terms of use',
    },
    emailLink: {
      title: 'WindMaps Admin',
      confirming: 'Confirming your access…',
      validating: 'Validating your access link…',
      success: 'Signed in. Redirecting…',
      confirmEmailLabel: 'Confirm your email to finish signing in',
      emailPlaceholder: 'your-email@example.com',
      emailWhy: 'Firebase requires this for security (anti-phishing).',
      signIn: 'Sign in',
      failedTitle: "Couldn't sign in",
      goToLogin: 'Go to sign in',
      errors: {
        notConfigured: 'Firebase is not configured in this environment. Reload or contact support.',
        invalidLink: 'Invalid or expired link. Ask the administrator for a new invite.',
        expiredOrUsed: 'This link has expired or was already used. Ask the administrator for a new invite.',
        invalidEmail: 'Invalid email. Check the address you typed.',
        userDisabled: 'User disabled. Contact the administrator.',
        network: 'No connection. Check your internet and try again.',
        unexpected: 'Unexpected error. Try again in a moment.',
      },
    },
    guard: {
      restrictedTitle: 'Restricted access',
      restrictedBody: (role) => (
        <>
          This area requires the {role} role. Ask the organization to grant it to your account.
        </>
      ),
    },
  },
  es: {
    login: {
      subtitle: 'Usa tu cuenta de Google o el correo registrado por la organización.',
      firebaseNotConfigured: (env, file, doc) => (
        <>
          Firebase auth no está configurado en este build. Define {env} en {file} y vuelve a compilar. Consulta{' '}
          {doc}.
        </>
      ),
      continueWithGoogle: 'Continuar con Google',
      or: 'o',
      emailLabel: 'Correo electrónico',
      passwordLabel: 'Contraseña',
      signIn: 'Ingresar',
      privacyPolicy: 'Política de privacidad',
      terms: 'Términos de uso',
    },
    emailLink: {
      title: 'WindMaps Admin',
      confirming: 'Confirmando tu acceso…',
      validating: 'Validando el enlace de acceso…',
      success: 'Sesión iniciada. Redirigiendo…',
      confirmEmailLabel: 'Confirma tu correo para completar el acceso',
      emailPlaceholder: 'tu-correo@ejemplo.com',
      emailWhy: 'Firebase lo exige por seguridad (anti-phishing).',
      signIn: 'Ingresar',
      failedTitle: 'No se pudo ingresar',
      goToLogin: 'Ir al inicio de sesión',
      errors: {
        notConfigured: 'Firebase no está configurado en este entorno. Recarga o contacta al soporte.',
        invalidLink: 'Enlace inválido o vencido. Pide una nueva invitación al administrador.',
        expiredOrUsed: 'El enlace venció o ya se usó. Pide una nueva invitación al administrador.',
        invalidEmail: 'Correo inválido. Revisa la dirección que escribiste.',
        userDisabled: 'Usuario deshabilitado. Contacta al administrador.',
        network: 'Sin conexión. Revisa tu internet e inténtalo de nuevo.',
        unexpected: 'Error inesperado. Inténtalo de nuevo en unos instantes.',
      },
    },
    guard: {
      restrictedTitle: 'Acceso restringido',
      restrictedBody: (role) => (
        <>
          Esta área requiere el rol {role}. Pide a la organización que se lo asigne a tu cuenta.
        </>
      ),
    },
  },
});
